import React, { useState, useRef, useEffect, memo, useMemo, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  IconButton,
  Tooltip,
  Chip,
  Badge,
  Drawer,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Fab,
  Divider,
  Button,
  Tabs,
  Tab,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid } from
'@mui/material';
import {
  LocationOn as LocationIcon,
  Business as BuildingIcon,
  Layers as FloorIcon,
  MeetingRoom as RoomIcon,
  Home as UnitIcon,
  Public as SiteIcon,
  Devices as DevicesIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  Stairs as HallwayIcon,
  Bathroom as RestroomIcon,
  Inventory as StockIcon,
  ZoomIn as ZoomInIcon,
  ZoomOut as ZoomOutIcon,
  CenterFocusStrong as CenterIcon,
  Close as CloseIcon,
  Dashboard as DashboardIcon,
  History as HistoryIcon,
  Settings as SettingsIcon,
  BugReport as TicketIcon,
  Visibility as ViewIcon,
  Save as SaveIcon,
  ArrowBack as ArrowBackIcon } from
'@mui/icons-material';
import { styled, useTheme } from '@mui/material/styles';
import { useSnackbar } from 'notistack';
import { usePermissions } from '../../contexts/PermissionContext';
import DeviceTable from '../DeviceTable';
import DeviceDialog from '../DeviceDialog';
import ConfirmDialog from '../ConfirmDialog';
import CustomDataPanel from '../CustomDataPanel';
import TicketsTable from '../TicketsTable';
import LogsTable from '../LogsTable';
import locationService from '../../services/locationService';
import { devicesApi, ticketsApi } from '../../services/apiClient';import { useThemeSettings } from "../../contexts/ThemeContext";

const TreeContainer = styled(Box)(({ theme }) => ({
  position: 'relative',
  width: '100%',
  height: '100%',
  overflow: 'auto',
  background: theme.palette.mode === 'dark' ?
  `radial-gradient(circle at 1px 1px, ${theme.palette.grey[800]} 1px, transparent 1px)` :
  `radial-gradient(circle at 1px 1px, ${theme.palette.grey[300]} 1px, transparent 1px)`,
  backgroundSize: '20px 20px',
  '&::-webkit-scrollbar': {
    width: '8px',
    height: '8px'
  },
  '&::-webkit-scrollbar-track': {
    background: theme.palette.background.paper
  },
  '&::-webkit-scrollbar-thumb': {
    background: theme.palette.action.disabled,
    borderRadius: '4px'
  }
}));

const ZoomableContainer = styled(Box)(({ theme }) => ({
  transformOrigin: '0 0',
  transition: 'transform 0.2s ease',
  minHeight: '100%',
  minWidth: '100%',
  padding: theme.spacing(8)
}));

const NodeContainer = styled(Box)(({ theme }) => ({
  position: 'relative',
  display: 'inline-block'
}));

const NodeCard = styled(Paper)(({ theme, selected }) => ({
  padding: theme.spacing(1.5, 2),
  borderRadius: theme.spacing(1),
  cursor: 'pointer',
  transition: 'all 0.3s ease',
  background: theme.palette.background.paper,
  border: `2px solid ${selected ? theme.palette.primary.main : theme.palette.divider}`,
  boxShadow: selected ? theme.shadows[8] : theme.shadows[2],
  minWidth: '180px',
  position: 'relative',
  zIndex: 2,
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: theme.shadows[6],
    borderColor: theme.palette.primary.light
  }
}));

const ChildrenContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexWrap: 'wrap',
  justifyContent: 'center',
  gap: theme.spacing(4),
  marginTop: theme.spacing(10),
  position: 'relative'
}));

const ConnectionSvg = styled('svg')(({ theme }) => ({
  position: 'absolute',
  top: 0,
  left: 0,
  width: '100%',
  height: '100%',
  pointerEvents: 'none',
  zIndex: 1
}));

const ZoomControls = styled(Box)(({ theme }) => ({
  position: 'absolute',
  bottom: theme.spacing(2),
  right: theme.spacing(2),
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(1),
  zIndex: 10
}));

const getLocationIcon = (locationType, friendlyName) => {const { t } = useThemeSettings();
  const nameLower = friendlyName?.toLowerCase() || '';

  // Type-based mapping using numeric values
  if (locationType === 1) return <SiteIcon />;
  if (locationType === 2) return <BuildingIcon />;
  if (locationType === 3 || nameLower.includes('verdieping')) return <FloorIcon />;
  if (locationType === 4) return <UnitIcon />;
  if (locationType === 5 || nameLower.includes('kamer')) return <RoomIcon />;
  if (locationType === 6 || nameLower.includes('gang') || nameLower.includes('hal')) return <HallwayIcon />;
  if (locationType === 7 || nameLower.includes('badkamer') || nameLower.includes('toilet') || nameLower.includes('wc')) return <RestroomIcon />;
  if (locationType === 8 || nameLower.includes('voorraad') || nameLower.includes('magazijn')) return <StockIcon />;

  // Additional name-based detection
  if (nameLower.includes('aw ')) return <UnitIcon />;

  // Default icon
  return <LocationIcon />;
};

const getLocationColor = (locationType) => {
  const colorMap = {
    1: 'primary', // Site
    2: 'secondary', // Building
    3: 'info', // Floor
    4: 'success', // Unit
    5: 'success', // Room
    6: 'warning', // Hallway
    7: 'default', // Restroom
    8: 'warning' // Stock
  };
  return colorMap[locationType] || 'default';
};

const TreeNode = ({ node, onSelect, onEdit, onDelete, onAdd, selectedId, parentRef, level = 0 }) => {const { t } = useThemeSettings();
  const theme = useTheme();
  const nodeRef = useRef(null);
  const { hasPermission } = usePermissions();
  const nodeId = node.id || node._id || node.location_UUID;
  const nodeName = node.friendlyName || node.name || node.title || 'Unnamed Location';
  const isSelected = selectedId === nodeId;
  const hasChildren = node.children && node.children.length > 0;
  const [expanded, setExpanded] = useState(level === 0);

  // Optimized SVG connection rendering - only update when necessary
  // Using ResizeObserver would be even better but this is a good balance
  useEffect(() => {
    if (!parentRef || !nodeRef.current || level === 0) return;

    const updateConnection = () => {
      const svg = document.getElementById(`svg-${nodeId}`);
      if (!svg) return;

      const parentRect = parentRef.getBoundingClientRect();
      const nodeRect = nodeRef.current.getBoundingClientRect();
      const containerRect = svg.parentElement.getBoundingClientRect();

      const startX = parentRect.left - containerRect.left + parentRect.width / 2;
      const startY = parentRect.bottom - containerRect.top;
      const endX = nodeRect.left - containerRect.left + nodeRect.width / 2;
      const endY = nodeRect.top - containerRect.top;

      const path = svg.querySelector('path');
      if (path) {
        const midY = (startY + endY) / 2;
        path.setAttribute('d', `M ${startX} ${startY} C ${startX} ${midY}, ${endX} ${midY}, ${endX} ${endY}`);
      }
    };

    // Initial render
    updateConnection();

    // Optional: Re-calculate on window resize (debounced)
    let timeoutId;
    const handleResize = () => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(updateConnection, 150);
    };

    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
      clearTimeout(timeoutId);
    };
  }, [nodeId, parentRef, level, isSelected]); // Only re-render when node is selected

  return (
    <NodeContainer>
      {level > 0 &&
      <ConnectionSvg id={`svg-${nodeId}`}>
          <path
          stroke={theme.palette.divider}
          strokeWidth="2"
          fill="none"
          strokeDasharray="5,3" />

        </ConnectionSvg>
      }

      <NodeCard
        ref={nodeRef}
        selected={isSelected}
        onClick={() => onSelect(node)}
        elevation={isSelected ? 4 : 1}>
        
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
          <Box sx={{ color: `${getLocationColor(node.locationType)}.main` }}>
            {getLocationIcon(node.locationType, nodeName)}
          </Box>
          <Typography variant="subtitle1" sx={{ fontWeight: 600, flex: 1 }}>
            {nodeName}
          </Typography>
          {node.deviceCount > 0 &&
          <Badge badgeContent={node.deviceCount} color="primary" max={99}>
              <DevicesIcon fontSize="small" />
            </Badge>
          }
        </Box>

        {node.description &&
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
            {node.description}
          </Typography>
        }

        <Box sx={{ display: 'flex', gap: 0.5, mt: 1 }}>
          <Chip
            label={`Type ${node.locationType || 'Unknown'}`}
            size="small"
            color={getLocationColor(node.locationType)}
            variant="outlined" />

          {hasChildren &&
          <Chip
            label={`${node.children.length} ${expanded ? '▾' : '▸'}`}
            size="small"
            variant="filled"
            onClick={(e) => {
              e.stopPropagation();
              setExpanded(!expanded);
            }}
            sx={{ backgroundColor: 'action.hover', cursor: 'pointer' }} />

          }
        </Box>

        <Box sx={{
          position: 'absolute',
          top: 8,
          right: 8,
          display: 'flex',
          gap: 0.5,
          opacity: isSelected ? 1 : 0,
          transition: 'opacity 0.2s',
          '&:hover': { opacity: 1 }
        }}>
          {hasPermission('locations.create') &&
          <Tooltip title={t ? t("HierarchicalLocationView.add_sublocation") : "Add sublocation"}>
              <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                onAdd && onAdd(node);
              }}
              sx={{
                backgroundColor: 'background.paper',
                '&:hover': { backgroundColor: 'primary.light', color: 'primary.contrastText' }
              }}>
              
                <AddIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          }
          {hasPermission('locations.edit') &&
          <Tooltip title={t ? t("DeviceTable.edit") : "Edit"}>
              <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                onEdit && onEdit(node);
              }}
              sx={{
                backgroundColor: 'background.paper',
                '&:hover': { backgroundColor: 'info.light', color: 'info.contrastText' }
              }}>
              
                <EditIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          }
          {hasPermission('locations.delete') && !node.children?.length && !node.deviceCount &&
          <Tooltip title={t ? t("DeviceTable.delete") : "Delete"}>
              <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                onDelete && onDelete(node);
              }}
              sx={{
                backgroundColor: 'background.paper',
                '&:hover': { backgroundColor: 'error.light', color: 'error.contrastText' }
              }}>
              
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          }
        </Box>
      </NodeCard>

      {hasChildren && expanded &&
      <ChildrenContainer>
          {node.children.map((child) =>
        <MemoizedTreeNode
          key={child.id || child._id || child.location_UUID}
          node={child}
          onSelect={onSelect}
          onEdit={onEdit}
          onDelete={onDelete}
          onAdd={onAdd}
          selectedId={selectedId}
          parentRef={nodeRef.current}
          level={level + 1} />

        )}
        </ChildrenContainer>
      }
    </NodeContainer>);

};

// Memoize TreeNode to prevent unnecessary re-renders
const MemoizedTreeNode = memo(TreeNode, (prevProps, nextProps) => {
  // Custom comparison function - only re-render if these change
  return (
    prevProps.selectedId === nextProps.selectedId &&
    prevProps.node === nextProps.node &&
    prevProps.level === nextProps.level
  );
});

const VisualTreeView = ({
  locations,
  selectedLocationId,
  onLocationSelect,
  onLocationEdit,
  onLocationDelete,
  onLocationAdd,
  selectedLocation
}) => {const { t } = useThemeSettings();
  const theme = useTheme();
  const [zoom, setZoom] = useState(1);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerTab, setDrawerTab] = useState(0);
  const [devices, setDevices] = useState([]);
  const [loadingDevices, setLoadingDevices] = useState(false);
  const [deviceDialogOpen, setDeviceDialogOpen] = useState(false);
  const [deviceDialogMode, setDeviceDialogMode] = useState('create');
  const [deviceToEdit, setDeviceToEdit] = useState(null);
  const [deleteDeviceConfirmOpen, setDeleteDeviceConfirmOpen] = useState(false);
  const [deviceToDelete, setDeviceToDelete] = useState(null);

  // Ticket-related states
  const [ticketViewDialogOpen, setTicketViewDialogOpen] = useState(false);
  const [ticketEditDialogOpen, setTicketEditDialogOpen] = useState(false);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [deleteTicketConfirmOpen, setDeleteTicketConfirmOpen] = useState(false);
  const [ticketToDelete, setTicketToDelete] = useState(null);
  const [ticketLocations, setTicketLocations] = useState([]);

  const containerRef = useRef(null);
  const { hasPermission } = usePermissions();

  useEffect(() => {
    if (selectedLocation) {
      setDrawerOpen(true);
      loadDevices(selectedLocation);
    }
  }, [selectedLocation]);

  const handleAddDevice = () => {
    setDeviceToEdit(null);
    setDeviceDialogMode('create');
    setDeviceDialogOpen(true);
  };

  const handleEditDevice = (device) => {
    setDeviceToEdit(device);
    setDeviceDialogMode('edit');
    setDeviceDialogOpen(true);
  };

  const handleDeleteDevice = (device) => {
    setDeviceToDelete(device);
    setDeleteDeviceConfirmOpen(true);
  };

  const handleDeviceSave = async () => {
    setDeviceDialogOpen(false);
    // Refresh devices
    if (selectedLocation) {
      await loadDevices(selectedLocation);
    }
  };

  const handleConfirmDeviceDelete = async () => {
    if (deviceToDelete) {
      try {
        const deviceId = deviceToDelete.id || deviceToDelete._id || deviceToDelete.device_UUID;
        await devicesApi.delete(deviceId);
        setDeleteDeviceConfirmOpen(false);
        setDeviceToDelete(null);
        // Refresh devices
        if (selectedLocation) {
          await loadDevices(selectedLocation);
        }
      } catch (error) {
        console.error('Error deleting device:', error);
      }
    }
  };

  const loadDevices = async (location) => {
    if (!location) return;

    setLoadingDevices(true);
    try {
      const locationId = location.id || location._id || location.location_UUID;
      const response = await locationService.getLocationDevices(locationId);
      const deviceData = response?.data || response || [];
      setDevices(Array.isArray(deviceData) ? deviceData : []);
    } catch (error) {
      console.error('Error loading devices:', error);
      // Use mock data as fallback for now
      const mockDevices = [
      {
        id: 1,
        friendlyName: 'Device 1',
        config: { status: 'online' },
        deviceTypeModel: { friendlyName: 'Sensor' },
        last_seen: new Date().toISOString()
      },
      {
        id: 2,
        friendlyName: 'Device 2',
        config: { status: 'offline' },
        deviceTypeModel: { friendlyName: 'Camera' },
        last_seen: new Date().toISOString()
      }];

      setDevices(mockDevices);
    } finally {
      setLoadingDevices(false);
    }
  };

  // Ticket handlers
  const handleViewTicket = (ticket) => {
    setSelectedTicket(ticket);
    setTicketViewDialogOpen(true);
  };

  const handleEditTicket = (ticket) => {
    setSelectedTicket(ticket);
    setTicketEditDialogOpen(true);
  };

  const handleDeleteTicket = (ticketId) => {
    const ticket = { id: ticketId };
    setTicketToDelete(ticket);
    setDeleteTicketConfirmOpen(true);
  };

  const handleConfirmTicketDelete = async () => {
    if (ticketToDelete) {
      try {
        await ticketsApi.delete(ticketToDelete.id);
        setDeleteTicketConfirmOpen(false);
        setTicketToDelete(null);
        // Refresh tickets table if needed
        // This will be handled by the TicketsTable component internally
      } catch (error) {
        console.error('Error deleting ticket:', error);
      }
    }
  };

  const handleTicketEditSave = async () => {
    if (!selectedTicket) return;

    try {
      const updateData = {
        title: selectedTicket.title,
        description: selectedTicket.description,
        steps_tried: selectedTicket.steps_tried,
        status: selectedTicket.status,
        urgency: selectedTicket.urgency,
        support_type: selectedTicket.support_type,
        location_id: selectedTicket.locationId,
        next_step: selectedTicket.next_step
      };

      await ticketsApi.update(selectedTicket.id, updateData);
      setTicketEditDialogOpen(false);
      setSelectedTicket(null);
      // The TicketsTable component will handle refreshing the data
    } catch (error) {
      console.error('Error updating ticket:', error);
    }
  };

  // Helper functions for tickets
  const renderUrgencyChip = (urgency) => {const { t } = useThemeSettings();
    let color = 'default';
    switch (urgency) {
      case 'LOW':
        color = 'info';
        break;
      case 'MEDIUM':
        color = 'warning';
        break;
      case 'HIGH':
      case 'URGENT':
        color = 'error';
        break;
      default:
        color = 'default';
    }
    return <Chip label={urgency} color={color} size="small" />;
  };

  const renderStatusChip = (status) => {const { t } = useThemeSettings();
    let color = 'default';
    switch (status) {
      case 'Open':
        color = 'info';
        break;
      case 'In Progress':
        color = 'warning';
        break;
      case 'Closed':
        color = 'success';
        break;
      default:
        color = 'default';
    }
    return <Chip label={status} color={color} size="small" />;
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    }).format(date);
  };

  const handleZoomIn = () => {
    setZoom((prev) => Math.min(prev + 0.1, 2));
  };

  const handleZoomOut = () => {
    setZoom((prev) => Math.max(prev - 0.1, 0.5));
  };

  const handleResetZoom = () => {
    setZoom(1);
  };

  // Memoize handlers to prevent unnecessary re-renders
  const handleLocationSelect = useCallback((location) => {
    onLocationSelect(location);
    setDrawerOpen(true);
    setDrawerTab(0); // Reset to overview tab
  }, [onLocationSelect]);

  if (!locations || !Array.isArray(locations) || locations.length === 0) {
    return (
      <Box sx={{ textAlign: 'center', p: 4 }}>
        <LocationIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
        <Typography variant="h6" color="text.secondary">{t ? t("VisualTreeView.no_locations_available") : "No locations available"}

        </Typography>
        <Typography variant="body2" color="text.secondary">{t ? t("VisualTreeView.create_your_first_location_get_starte") : "Create your first location to get started"}

        </Typography>
      </Box>);

  }

  return (
    <Box sx={{ position: 'relative', width: '100%', height: '100%' }}>
      <TreeContainer ref={containerRef}>
        <ZoomableContainer sx={{ transform: `scale(${zoom})` }}>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: 8 }}>
            {locations.map((location) =>
            <MemoizedTreeNode
              key={location.id || location._id || location.location_UUID}
              node={location}
              onSelect={handleLocationSelect}
              onEdit={onLocationEdit}
              onDelete={onLocationDelete}
              onAdd={onLocationAdd}
              selectedId={selectedLocationId}
              parentRef={null}
              level={0} />

            )}
          </Box>
        </ZoomableContainer>
      </TreeContainer>

      {/* Zoom Controls */}
      <ZoomControls>
        <Fab size="small" onClick={handleZoomIn} color="primary">
          <ZoomInIcon />
        </Fab>
        <Fab size="small" onClick={handleZoomOut} color="primary">
          <ZoomOutIcon />
        </Fab>
        <Fab size="small" onClick={handleResetZoom} color="secondary">
          <CenterIcon />
        </Fab>
      </ZoomControls>

      {/* Location Details Drawer */}
      <Drawer
        anchor="right"
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        PaperProps={{
          sx: { width: 480 }
        }}>
        
        {selectedLocation &&
        <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
            {/* Header */}
            <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                {/* Location icon and name */}
                <Box sx={{ display: 'flex', alignItems: 'center' }}>

                  <Box sx={{ color: `${getLocationColor(selectedLocation.locationType)}.main` }}>
                    {getLocationIcon(selectedLocation.locationType, selectedLocation.name)}
                  </Box>
                  <Typography variant="h6">
                    {selectedLocation.friendlyName || selectedLocation.name || 'Location Details'}
                  </Typography>
                </Box>
                <IconButton onClick={() => setDrawerOpen(false)}>
                  <CloseIcon />
                </IconButton>
              </Box>
            </Box>

            {/* Tabs */}
            <Tabs
            value={drawerTab}
            onChange={(e, v) => setDrawerTab(v)}
            sx={{ borderBottom: 1, borderColor: 'divider' }}>
            
              <Tab label="" icon={<DashboardIcon />} iconPosition="start" />
              <Tab label="" icon={<DevicesIcon />} iconPosition="start" />
              <Tab label="" icon={<TicketIcon />} iconPosition="start" />
              <Tab label="" icon={<HistoryIcon />} iconPosition="start" />
              <Tab label="" icon={<SettingsIcon />} iconPosition="start" />
            </Tabs>

            {/* Tab Content */}
            <Box sx={{ flex: 1, overflow: 'auto', p: 2 }}>
              {/* Overview Tab */}
              {drawerTab === 0 &&
            <Box>
                  <Card sx={{ mb: 2 }}>
                    <CardContent>
                      <Typography variant="subtitle2" color="text.secondary" gutterBottom>{t ? t("LocationDetails.location_information") : "Location Information"}

                  </Typography>
                      <List dense>
                        <ListItem>
                          <ListItemText
                        primary="Type"
                        secondary={`Type ${selectedLocation.locationType || 'Unknown'}`} />
                      
                        </ListItem>
                        <ListItem>
                          <ListItemText
                        primary="ID"
                        secondary={selectedLocation.id || selectedLocation._id || selectedLocation.location_UUID} />
                      
                        </ListItem>
                        {selectedLocation.description &&
                    <ListItem>
                            <ListItemText
                        primary="Description"
                        secondary={selectedLocation.description} />
                      
                          </ListItem>
                    }
                        <ListItem>
                          <ListItemText
                        primary="Created"
                        secondary={selectedLocation.createdAt ? new Date(selectedLocation.createdAt).toLocaleDateString() : 'N/A'} />
                      
                        </ListItem>
                      </List>
                    </CardContent>
                  </Card>

                  <Card sx={{ mb: 2 }}>
                    <CardContent>
                      <Typography variant="subtitle2" color="text.secondary" gutterBottom>{t ? t("LocationDetails.statistics") : "Statistics"}

                  </Typography>
                      <List dense>
                        <ListItem>
                          <ListItemIcon>
                            <DevicesIcon />
                          </ListItemIcon>
                          <ListItemText
                        primary="Devices"
                        secondary={`${selectedLocation.deviceCount || 0} devices in this location`} />
                      
                        </ListItem>
                        <ListItem>
                          <ListItemIcon>
                            <LocationIcon />
                          </ListItemIcon>
                          <ListItemText
                        primary="Sublocations"
                        secondary={`${selectedLocation.children?.length || 0} direct sublocations`} />
                      
                        </ListItem>
                      </List>
                    </CardContent>
                  </Card>

                  {/* Custom Data Panel */}
                  <CustomDataPanel
                title={t ? t("VisualTreeView.custom_location_data") : "Custom Location Data"}
                data={selectedLocation?.data || []}
                entityType="location"
                entityId={selectedLocation?.id || selectedLocation?._id || selectedLocation?.location_UUID}
                onSave={async (newData) => {
                  try {
                    const locationId = selectedLocation.id || selectedLocation._id || selectedLocation.location_UUID;
                    await locationService.updateLocation(
                      locationId,
                      { data: newData }
                    );

                    // Update the selected location with new data
                    const updatedLocation = {
                      ...selectedLocation,
                      data: newData
                    };

                    // Update in the locations array if onUpdate is available
                    if (onUpdate) {
                      onUpdate(updatedLocation);
                    }

                    // Refresh the selection to show updated data
                    if (onLocationSelect) {
                      onLocationSelect(updatedLocation);
                    }

                    return true; // Success indicator for CustomDataPanel
                  } catch (error) {
                    console.error('Error updating location data:', error);
                    throw error; // Re-throw to let CustomDataPanel handle it
                  }
                }} />
              
                </Box>
            }

              {/* Devices Tab */}
              {drawerTab === 1 &&
            <Box>
                  <DeviceTable
                devices={devices}
                loading={loadingDevices}
                locationId={selectedLocation?.id || selectedLocation?._id || selectedLocation?.location_UUID}
                onEditDevice={handleEditDevice}
                onDeleteDevice={handleDeleteDevice}
                onAddDevice={handleAddDevice} />
              
                </Box>
            }

              {/* History Tab */}
              {drawerTab === 3 &&
            <Box>
                  <LogsTable
                locationId={selectedLocation?.id || selectedLocation?._id || selectedLocation?.location_UUID} />
              
                </Box>
            }

              {/* Settings Tab */}
              {drawerTab === 4 &&
            <Box>
                  <Card sx={{ mb: 2 }}>
                    <CardContent>
                      <Typography variant="subtitle2" color="text.secondary" gutterBottom>{t ? t("LocationManagement.location_settings") : "Location Settings"}

                  </Typography>
                      <Alert severity="info">{t ? t("VisualTreeView.locationspecific_settings_permission") : "Location-specific settings and permissions will be displayed here"}

                  </Alert>
                    </CardContent>
                  </Card>

                  <Box sx={{ display: 'flex', gap: 1, flexDirection: 'column' }}>
                    {onLocationEdit &&
                <Button
                  variant="outlined"
                  startIcon={<EditIcon />}
                  onClick={() => onLocationEdit(selectedLocation)}
                  fullWidth>{t ? t("LocationManagement.edit_location") : "Edit Location"}


                </Button>
                }
                    {onLocationAdd &&
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={() => onLocationAdd(selectedLocation)}
                  fullWidth>{t ? t("HierarchicalLocationView.add_sublocation_1") : "Add Sublocation"}


                </Button>
                }
                    {onLocationDelete && !selectedLocation.children?.length && !selectedLocation.deviceCount &&
                <Button
                  variant="outlined"
                  color="error"
                  startIcon={<DeleteIcon />}
                  onClick={() => onLocationDelete(selectedLocation)}
                  fullWidth>{t ? t("LocationManagement.delete_location") : "Delete Location"}


                </Button>
                }
                  </Box>
                </Box>
            }

              {/* Tickets Tab */}
              {drawerTab === 2 &&
            <Box>
                  <TicketsTable
                locationId={selectedLocation?.id || selectedLocation?._id || selectedLocation?.location_UUID}
                onViewTicket={handleViewTicket}
                onEditTicket={handleEditTicket}
                onDeleteTicket={handleDeleteTicket} />
              
                </Box>
            }
            </Box>
          </Box>
        }
      </Drawer>

      <DeviceDialog
        open={deviceDialogOpen}
        mode={deviceDialogMode}
        device={deviceToEdit}
        onClose={() => setDeviceDialogOpen(false)}
        onSave={handleDeviceSave} />
      

      <ConfirmDialog
        open={deleteDeviceConfirmOpen}
        title={t ? t("DeviceTable.delete_device") : "Delete Device"}
        content={`Are you sure you want to delete "${deviceToDelete?.friendlyName || 'this device'}"? This action cannot be undone.`}
        confirmText={t ? t("DeviceTable.delete") : "Delete"}
        confirmColor="error"
        onConfirm={handleConfirmDeviceDelete}
        onCancel={() => setDeleteDeviceConfirmOpen(false)} />
      

      {/* Ticket View Dialog */}
      <Dialog
        open={ticketViewDialogOpen}
        onClose={() => setTicketViewDialogOpen(false)}
        maxWidth="md"
        fullWidth>
        
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Typography variant="h6">{t ? t("VisualTreeView.ticket") : "Ticket #"}{selectedTicket?.id}</Typography>
            <Box sx={{ ml: 2 }}>
              {selectedTicket && renderStatusChip(selectedTicket.status)}
            </Box>
          </Box>
          <IconButton onClick={() => setTicketViewDialogOpen(false)}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          {selectedTicket &&
          <Box sx={{ pt: 1 }}>
              <Typography variant="h6" gutterBottom>
                {selectedTicket.title || 'No title provided'}
              </Typography>

              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>{t ? t("LocationDialog.description") : "Description"}

              </Typography>
                <Typography variant="body1" paragraph>
                  {selectedTicket.description}
                </Typography>
              </Box>

              {selectedTicket.steps_tried &&
            <Box sx={{ mb: 3 }}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>{t ? t("VisualTreeView.steps_already_tried") : "Steps Already Tried"}

              </Typography>
                  <Typography variant="body1" paragraph>
                    {selectedTicket.steps_tried}
                  </Typography>
                </Box>
            }

              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>{t ? t("VisualTreeView.urgency") : "Urgency"}

                </Typography>
                  {renderUrgencyChip(selectedTicket.urgency)}
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>{t ? t("VisualTreeView.support_type") : "Support Type"}

                </Typography>
                  <Chip label={selectedTicket.support_type?.replace('_', ' ')} size="small" variant="outlined" />
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>{t ? t("LocationDetails.created") : "Created"}

                </Typography>
                  <Typography variant="body2">
                    {formatDate(selectedTicket.created_at)}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>{t ? t("LocationDetails.last_updated") : "Last Updated"}

                </Typography>
                  <Typography variant="body2">
                    {formatDate(selectedTicket.updated_at)}
                  </Typography>
                </Grid>
                {selectedTicket.next_step &&
              <Grid item xs={12}>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>{t ? t("VisualTreeView.next_step") : "Next Step"}

                </Typography>
                    <Typography variant="body2">
                      {selectedTicket.next_step}
                    </Typography>
                  </Grid>
              }
              </Grid>
            </Box>
          }
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTicketViewDialogOpen(false)}>{t ? t("LocationManagement.close") : "Close"}

          </Button>
          <Button
            variant="contained"
            startIcon={<EditIcon />}
            onClick={() => {
              setTicketViewDialogOpen(false);
              setTicketEditDialogOpen(true);
            }}>{t ? t("DeviceTable.edit") : "Edit"}


          </Button>
        </DialogActions>
      </Dialog>

      {/* Ticket Edit Dialog */}
      <Dialog
        open={ticketEditDialogOpen}
        onClose={() => setTicketEditDialogOpen(false)}
        maxWidth="md"
        fullWidth>
        
        <DialogTitle>{t ? t("VisualTreeView.edit_ticket") : "Edit Ticket #"}
          {selectedTicket?.id}
        </DialogTitle>
        <DialogContent>
          {selectedTicket &&
          <Grid container spacing={3} sx={{ mt: 1 }}>
              <Grid item xs={12}>
                <TextField
                fullWidth
                label={t ? t("VisualTreeView.title") : "Title"}
                value={selectedTicket.title || ''}
                onChange={(e) => setSelectedTicket({ ...selectedTicket, title: e.target.value })} />
              
              </Grid>

              <Grid item xs={12}>
                <TextField
                fullWidth
                label={t ? t("LocationDialog.description") : "Description"}
                value={selectedTicket.description || ''}
                onChange={(e) => setSelectedTicket({ ...selectedTicket, description: e.target.value })}
                multiline
                rows={4}
                required />
              
              </Grid>

              <Grid item xs={12}>
                <TextField
                fullWidth
                label={t ? t("VisualTreeView.steps_already_tried") : "Steps Already Tried"}
                value={selectedTicket.steps_tried || ''}
                onChange={(e) => setSelectedTicket({ ...selectedTicket, steps_tried: e.target.value })}
                multiline
                rows={3} />
              
              </Grid>

              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>{t ? t("DeviceTable.status") : "Status"}</InputLabel>
                  <Select
                  value={selectedTicket.status || 'Open'}
                  label={t ? t("DeviceTable.status") : "Status"}
                  onChange={(e) => setSelectedTicket({ ...selectedTicket, status: e.target.value })}>
                  
                    <MenuItem value="Open">{t ? t("VisualTreeView.open") : "Open"}</MenuItem>
                    <MenuItem value="In Progress">{t ? t("VisualTreeView.progress") : "In Progress"}</MenuItem>
                    <MenuItem value="Closed">{t ? t("VisualTreeView.closed") : "Closed"}</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>{t ? t("VisualTreeView.urgency") : "Urgency"}</InputLabel>
                  <Select
                  value={selectedTicket.urgency || 'MEDIUM'}
                  label={t ? t("VisualTreeView.urgency") : "Urgency"}
                  onChange={(e) => setSelectedTicket({ ...selectedTicket, urgency: e.target.value })}>
                  
                    <MenuItem value="LOW">{t ? t("VisualTreeView.low") : "Low"}</MenuItem>
                    <MenuItem value="MEDIUM">{t ? t("VisualTreeView.medium") : "Medium"}</MenuItem>
                    <MenuItem value="HIGH">{t ? t("VisualTreeView.high") : "High"}</MenuItem>
                    <MenuItem value="URGENT">{t ? t("VisualTreeView.urgent") : "Urgent"}</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>{t ? t("VisualTreeView.support_type") : "Support Type"}</InputLabel>
                  <Select
                  value={selectedTicket.support_type || 'other'}
                  label={t ? t("VisualTreeView.support_type") : "Support Type"}
                  onChange={(e) => setSelectedTicket({ ...selectedTicket, support_type: e.target.value })}>
                  
                    <MenuItem value="domotics">{t ? t("VisualTreeView.domotics") : "Domotics"}</MenuItem>
                    <MenuItem value="access_control">{t ? t("VisualTreeView.access_control") : "Access Control"}</MenuItem>
                    <MenuItem value="tablet">{t ? t("VisualTreeView.tablet") : "Tablet"}</MenuItem>
                    <MenuItem value="emergency_system">{t ? t("VisualTreeView.emergency_system") : "Emergency System"}</MenuItem>
                    <MenuItem value="it_pc">{t ? t("VisualTreeView.itpc") : "IT/PC"}</MenuItem>
                    <MenuItem value="other">{t ? t("VisualTreeView.other") : "Other"}</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12}>
                <TextField
                fullWidth
                label={t ? t("VisualTreeView.next_step") : "Next Step"}
                value={selectedTicket.next_step || ''}
                onChange={(e) => setSelectedTicket({ ...selectedTicket, next_step: e.target.value })} />
              
              </Grid>
            </Grid>
          }
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTicketEditDialogOpen(false)}>{t ? t("LocationDialog.cancel") : "Cancel"}

          </Button>
          <Button
            variant="contained"
            startIcon={<SaveIcon />}
            onClick={handleTicketEditSave}>{t ? t("LocationManagement.save_changes") : "Save Changes"}


          </Button>
        </DialogActions>
      </Dialog>

      {/* Ticket Delete Confirmation */}
      <ConfirmDialog
        open={deleteTicketConfirmOpen}
        title={t ? t("VisualTreeView.delete_ticket") : "Delete Ticket"}
        content={`Are you sure you want to delete ticket #${ticketToDelete?.id}? This action cannot be undone.`}
        confirmText={t ? t("DeviceTable.delete") : "Delete"}
        confirmColor="error"
        onConfirm={handleConfirmTicketDelete}
        onCancel={() => setDeleteTicketConfirmOpen(false)} />
      
    </Box>);

};

export default VisualTreeView;